/** The itinerary's fact band (plan §2 "Fact band", §5 "Band"): the record fact band
 *  (`records/band/FactBand`, the account band's styling) over the itinerary's cells. A detail
 *  that is an info note (an app button the app cannot act on) carries an info icon — never
 *  red.
 *
 *  Each cell leads to what owns its facts: a page tab (a real link) or a card on the record
 *  (a button — `onSelect` scrolls to and focuses it). A cell whose owner the viewer cannot
 *  reach is plain text. Above the band, the edge states (O9) as banners: a placeholder, no
 *  catalog itinerary, a shared default (`ItineraryNotices` — the record page draws them above
 *  its tabs instead, so they show on every tab: `notices={false}`). Cells and banners come
 *  from `itineraryFactBandCells` / `itineraryNotices`, over the detail payload and the used-by
 *  summary. */
import CropFreeIcon from '@mui/icons-material/CropFree';
import { Alert } from '@mui/material';
import { useMemo, type ReactElement } from 'react';

import type { ItineraryDetail, ItineraryUsedBySummary } from '@/api/itineraries';
import { FactBand, type FactBandTarget } from '@/features/records/band/FactBand';
import type { RecordEditorMode } from '@/features/records/editor/types';

import {
  itineraryFactBandCells,
  itineraryNotices,
  type ItineraryBandCell,
  type ItineraryCardKey,
  type ItineraryNoticeId,
  type ItineraryRecordTab,
} from './itineraryFactBandModel';

const ALL_TABS: ReadonlySet<ItineraryRecordTab> = new Set([
  'itinerary',
  'images',
  'usage',
  'history',
]);

/** The placeholder banner carries the status chip's icon; the others the severity's. */
const NOTICE_ICON: Partial<Record<ItineraryNoticeId, ReactElement>> = {
  placeholder: <CropFreeIcon fontSize="inherit" />,
};

export interface ItineraryFactBandProps {
  detail: ItineraryDetail;
  /** The used-by summary (D7, `usedBySummary`), read lazily after the detail; absent → the
   *  detail's counts only. */
  summary?: ItineraryUsedBySummary | null;
  now?: Date;
  /** The tabs the viewer sees: a cell links only to one of them. Default: all. */
  tabs?: ReadonlySet<ItineraryRecordTab>;
  hrefFor: (tab: ItineraryRecordTab) => string;
  /** Scroll to and focus a card of the record; absent → those cells are plain text. */
  onSelect?: (card: ItineraryCardKey) => void;
  /** Draw the edge-state banners above the band (default); false when the page draws
   *  `itineraryNotices` elsewhere. */
  notices?: boolean;
}

/** The edge states (O9) as banners, each when it applies: a placeholder → no catalog
 *  itinerary → a shared default. A standing state of the record, not news: notes, never alert
 *  live regions. `mode`: the page's (view: what creates a catalog itinerary is the
 *  dashboard's save). `gap`: the space under each (theme spacing). */
export function ItineraryNotices({
  detail,
  mode = 'view',
  gap = 1.5,
}: {
  detail: ItineraryDetail;
  mode?: RecordEditorMode;
  gap?: number;
}) {
  const banners = useMemo(() => itineraryNotices(detail, { mode }), [detail, mode]);
  return (
    <>
      {banners.map((n) => (
        <Alert
          key={n.id}
          role="note"
          severity={n.severity}
          {...(NOTICE_ICON[n.id] ? { icon: NOTICE_ICON[n.id] } : {})}
          sx={{ mb: gap }}
          data-itinerary-notice={n.id}
        >
          {n.text}
        </Alert>
      ))}
    </>
  );
}

export function ItineraryFactBand({
  detail,
  summary = null,
  now,
  tabs = ALL_TABS,
  hrefFor,
  onSelect,
  notices = true,
}: ItineraryFactBandProps) {
  const cells = useMemo(
    () => itineraryFactBandCells(detail, { summary, ...(now ? { now } : {}) }),
    [detail, summary, now],
  );
  const targetOf = ({ target: t }: ItineraryBandCell): FactBandTarget => {
    if (t.kind === 'tab') return tabs.has(t.tab) ? { to: hrefFor(t.tab) } : null;
    return onSelect ? { onClick: () => onSelect(t.card) } : null;
  };
  return (
    <>
      {notices && <ItineraryNotices detail={detail} />}
      <FactBand label="Itinerary summary" name="itinerary" cells={cells} targetOf={targetOf} />
    </>
  );
}
